import { useState, useEffect, useRef } from 'react';
import { Box, Button, Typography, Paper, Alert, TextField, Fade, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { QrCode as QrCodeIcon } from '@mui/icons-material';
import { Html5QrcodeScanner, Html5Qrcode, Html5QrcodeScanType } from 'html5-qrcode';
import BarcodeScannerNative from './BarcodeScannerNative';
import { 
  hasStoredPermission, 
  storePermission, 
  checkBrowserPermission, 
  getPermissionInstructions,
  shouldShowPermissionTips 
} from '../utils/cameraPermissions';

// A simple barcode scanner component
export default function BarcodeScanner({ 
  onScanSuccess, 
  onScanError,
  continuous = false,
  width = "100%",
  height = "250px",
  autoStart = false
}) {
  const [manualCode, setManualCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [scanner, setScanner] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [scanAttempt, setScanAttempt] = useState(0);
  const [permissionStatus, setPermissionStatus] = useState("unknown");
  const [useMobileScanner, setUseMobileScanner] = useState(null);
  const [hasExternalScanner, setHasExternalScanner] = useState(false);
  const [externalScannerMode, setExternalScannerMode] = useState(false);
  const scannerContainerRef = useRef(null);
  const externalScannerInputRef = useRef(null);
  
  // Detect mobile browsers - more specific detection
  const isMobileSafari = /iPhone|iPod|iPad/i.test(navigator.userAgent);
  const isMobileChrome = /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent) && /Mobile/i.test(navigator.userAgent);
  const isMobileFirefox = /Android/i.test(navigator.userAgent) && /Firefox/i.test(navigator.userAgent);
  const isMobile = isMobileSafari || isMobileChrome || isMobileFirefox;
  
  // Additional check for desktop Chrome being incorrectly detected
  const isDesktop = window.screen.width >= 1024 && window.screen.height >= 768;
  const actuallyMobile = isMobile && !isDesktop;
  
  // Detect external scanners using WebHID API
  useEffect(() => {
    const detectExternalScanner = async () => {
      try {
        // Check if WebHID is supported
        if ('hid' in navigator) {
          const devices = await navigator.hid.getDevices();
          
          // Look for barcode scanner devices (common vendor IDs for barcode scanners)
          const scannerDevices = devices.filter(device => {
            // Common barcode scanner vendor IDs
            const barcodeVendorIds = [
              0x05e0, // Symbol/Motorola
              0x0c2e, // Metrologic/Honeywell
              0x0536, // Hand Held Products (HHP)
              0x1234, // Generic HID scanners
              0x04b4, // Cypress (used by many scanner manufacturers)
              0x0403, // FTDI (used by some scanners)
              0x1a86, // QinHeng Electronics (common in Chinese scanners)
              0x0525  // Netchip Technology (some USB scanners)
            ];
            
            return barcodeVendorIds.includes(device.vendorId) ||
                   device.productName?.toLowerCase().includes('scanner') ||
                   device.productName?.toLowerCase().includes('barcode') ||
                   device.productName?.toLowerCase().includes('tera');
          });
          
          if (scannerDevices.length > 0) {
            console.log('🔍 External scanner detected:', scannerDevices);
            setHasExternalScanner(true);
            setExternalScannerMode(true);
          } else {
            console.log('🔍 No external scanner detected');
            setHasExternalScanner(false);
          }
        } else {
          console.log('🔍 WebHID not supported, checking for keyboard input patterns');
          // Fallback: assume external scanner if not mobile
          if (!actuallyMobile) {
            setHasExternalScanner(true);
            setExternalScannerMode(true);
          }
        }
      } catch (error) {
        console.log('🔍 Error detecting external scanner:', error);
        // Fallback for desktop environments
        if (!actuallyMobile) {
          setHasExternalScanner(true);
          setExternalScannerMode(true);
        }
      }
    };
    
    detectExternalScanner();
  }, [actuallyMobile]);
  
  // Use native scanner on mobile by default since standard has black screen issues
  useEffect(() => {
    if (useMobileScanner === null) {
      console.log('🔍 Mobile detection:', { isMobile, isDesktop, actuallyMobile, userAgent: navigator.userAgent });
      console.log('🔍 Setting useMobileScanner to:', actuallyMobile);
      setUseMobileScanner(actuallyMobile); // Use native scanner only on actual mobile devices
    }
  }, []); // Remove dependencies to prevent re-running
  
  // Initialize camera list and scanner
  useEffect(() => {
    const initCameras = async () => {
      try {
        // Skip camera initialization in test environment
        if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
          return;
        }
        
        // Check if MediaDevices API is available (don't request permission yet)
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Camera access not supported in this browser");
        }
        
        // Check stored permission and browser permission status
        const storedPermission = hasStoredPermission();
        const browserPermission = await checkBrowserPermission();
        
        console.log('📸 Permission check:', { storedPermission, browserPermission });
        
        if (storedPermission && browserPermission === 'granted') {
          setPermissionStatus('granted');
        } else if (browserPermission === 'denied') {
          setPermissionStatus('denied');
        } else {
          setPermissionStatus(browserPermission);
        }
        
        // Listen for permission changes if Permissions API is available
        if (navigator.permissions && browserPermission !== 'unsupported') {
          try {
            const permission = await navigator.permissions.query({ name: 'camera' });
            permission.addEventListener('change', () => {
              setPermissionStatus(permission.state);
              console.log('📸 Camera permission changed to:', permission.state);
            });
          } catch (permError) {
            console.warn('Could not listen for permission changes:', permError);
          }
        }
        
        // Test actual camera access to get real permission status
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({ 
            video: { facingMode: "environment" } 
          });
          testStream.getTracks().forEach(track => track.stop());
          console.log('📸 Camera access test: SUCCESS');
          setPermissionStatus('granted');
          storePermission(); // Store successful permission for persistence
        } catch (testError) {
          console.log('📸 Camera access test failed:', testError.name, testError.message);
          if (testError.name === 'NotAllowedError') {
            setPermissionStatus('denied');
          } else {
            setPermissionStatus('prompt');
          }
        }
        
        // Get available cameras
        const devices = await Html5Qrcode.getCameras();
        
        if (devices && devices.length) {
          setCameras(devices);
          
          // Select back camera by default (usually better for barcodes)
          const backCamera = devices.find(camera => {
            const label = camera.label.toLowerCase();
            return label.includes('back') || label.includes('rear') || label.includes('environment');
          });
          
          if (backCamera) {
            setSelectedCamera(backCamera.id);
          } else {
            // Otherwise use the last camera (often the back camera on mobile)
            setSelectedCamera(devices[devices.length - 1].id);
          }
        }
      } catch (err) {
        console.error("Error getting cameras:", err);
        
        // Provide specific error messages based on error type
        let errorMessage = "Unable to access camera.";
        
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = "Camera access denied. Please allow camera permissions in your browser settings.";
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = "No camera found. Please ensure your device has a camera.";
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = "Camera is already in use by another application.";
        } else if (err.name === 'OverconstrainedError') {
          errorMessage = "Camera doesn't support the required settings.";
        } else if (err.name === 'NotSupportedError') {
          errorMessage = "Camera access is not supported in this browser. Please use a modern browser.";
        } else if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          errorMessage = "Your browser doesn't support camera access. Please use HTTPS or a modern browser.";
        } else if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
          errorMessage = "Camera access requires HTTPS. Please use a secure connection.";
        }
        
        setError(errorMessage);
        if (onScanError) onScanError(errorMessage);
      }
    };
    
    initCameras();
    
    // Clean up the scanner when the component unmounts
    return () => {
      if (scanner) {
        try {
          scanner.clear();
          setScanner(null);
          setIsScanning(false);
          setSelectedCameraId('');
          console.log('BarcodeScanner cleaned up - camera stopped');
        } catch (err) {
          console.error('Error during scanner cleanup:', err);
        }
      }
    };
  }, [onScanError]);
  
  // Auto-focus barcode input field for immediate scanning
  useEffect(() => {
    if (externalScannerInputRef.current && !scanning) {
      // Always focus the barcode input field so external scanners work immediately
      setTimeout(() => {
        externalScannerInputRef.current?.focus();
      }, 100);
    }
  }, [externalScannerMode, scanning]);

  // Additional auto-focus when component mounts
  useEffect(() => {
    // Focus input field when component mounts for immediate barcode scanning
    const timer = setTimeout(() => {
      if (externalScannerInputRef.current) {
        externalScannerInputRef.current.focus();
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Ensure reader element is mounted before auto-start (only if not using external scanner)
  useEffect(() => {
    if (autoStart && selectedCamera && !scanning && !externalScannerMode) {
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        startScanner();
      }, 100);
    }
  }, [autoStart, selectedCamera, externalScannerMode]);
  
  // Debug: Log when component mounts/unmounts (reduced)
  useEffect(() => {
    console.log('🔍 BarcodeScanner mounted');
    return () => {
      console.log('🔍 BarcodeScanner unmounting');
    };
  }, []);
  
  // Start the scanner
  const startScanner = async () => {
    console.log('🔍 startScanner called');
    
    // Skip scanner start in test environment
    if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
      return;
    }
    
    // If using mobile scanner, don't start the regular scanner
    if (useMobileScanner) {
      console.log('🔍 Skipping regular scanner startup - using mobile scanner');
      return;
    }
    
    // Clean up any existing scanner
    if (scanner) {
      try {
        scanner.clear();
      } catch (e) {
        // Ignore errors
      }
    }
    
    try {
      setError(null);
      setScanning(true);
      setScanAttempt(prev => prev + 1); // Increment scan attempt counter
      
      // Wait for the reader element to be ready with retry logic
      let reader = null;
      let retries = 0;
      const maxRetries = 30; // Increased to 30 (3 seconds total)
      
      while (!reader && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Try multiple ways to get the reader element
        reader = scannerContainerRef.current || document.getElementById('reader');
        
        // If still not found, force a re-render check (reduced logging)
        if (!reader && retries === 16) {
          setError(null); // This will trigger a re-render
        }
        
        retries++;
        
        // Only log at specific intervals to reduce noise
        if (retries === 5 || retries === 15 || retries === maxRetries) {
          console.log(`🔍 Scanner retry ${retries}/${maxRetries}, reader found: ${!!reader}`);
        }
      }
      
      if (!reader) {
        setScanning(false);
        throw new Error('Scanner container not ready. Please close and reopen the scanner.');
      }
      const readerWidth = reader?.clientWidth || 500;
      const readerHeight = reader?.clientHeight || 350;
      
      // Make qrbox larger to ensure easier scanning
      const qrboxSize = Math.min(readerWidth, readerHeight) * 0.9; // 90% of container
      // Optimize dimensions for standard barcodes (wider than tall)
      const qrboxWidth = Math.min(Math.max(readerWidth * 0.7, 280), 320); // Between 280-320px wide
      const qrboxHeight = Math.min(qrboxWidth * 0.5, 160); // Shorter height for barcodes
      
      // Import the required constants 
      // These numbers correspond to the format enum in the library
      const formats = {
        QR_CODE: 0,
        AZTEC: 1,
        CODABAR: 2,
        CODE_39: 3,
        CODE_93: 4,
        CODE_128: 5,
        DATA_MATRIX: 6,
        MAXICODE: 7,
        ITF: 8,
        EAN_13: 9,
        EAN_8: 10,
        PDF_417: 11,
        RSS_14: 12,
        RSS_EXPANDED: 13,
        UPC_A: 14,
        UPC_E: 15,
        UPC_EAN_EXTENSION: 16
      };
      
      console.log('Starting scanner with camera:', selectedCamera);
      console.log('Container dimensions:', readerWidth, 'x', readerHeight);
      
      // Simplified configuration for better mobile compatibility
      const html5QrcodeScanner = new Html5QrcodeScanner(
        "reader",
        { 
          fps: 10,
          qrbox: { width: 250, height: 250 }, // Square for better compatibility
          aspectRatio: 1.0,             // Square aspect ratio
          showTorchButtonIfSupported: true,
          disableFlip: false,
          verbose: false,               // Disable verbose to reduce noise
          rememberLastUsedCamera: true,
          // Simplified constraints for mobile
          videoConstraints: {
            facingMode: "environment",  // Simplified constraint
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 }
          },
          // Explicit support for all common barcode formats
          formatsToSupport: [
            formats.QR_CODE,
            formats.CODE_39,
            formats.CODE_93,
            formats.CODE_128,
            formats.EAN_13,
            formats.EAN_8,
            formats.UPC_A,
            formats.UPC_E,
            formats.ITF,
            formats.CODABAR,
            formats.DATA_MATRIX,
            formats.AZTEC,
            formats.PDF_417
          ],
          // Remove experimental features that may cause issues
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA]
        },
        false
      );
      
      // Store the scanner instance
      setScanner(html5QrcodeScanner);
      
      console.log('About to render scanner...');
      console.log('Video constraints:', {
        facingMode: { ideal: "environment" },
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      });
      
      // Add a timeout to detect if the video stream never loads
      const renderTimeout = setTimeout(() => {
        console.error('Scanner render timeout - video may not be loading');
        const videoElement = document.querySelector('#reader video');
        if (videoElement) {
          console.log('Video element found but may be black:', {
            videoWidth: videoElement.videoWidth,
            videoHeight: videoElement.videoHeight,
            readyState: videoElement.readyState,
            networkState: videoElement.networkState
          });
        } else {
          console.log('No video element found in DOM');
        }
      }, 5000);
      
      // Start scanning with enhanced sensitivity handling
      html5QrcodeScanner.render(
        // Success callback with enhanced debounce for duplicate scans
        (decodedText, decodedResult) => {
          // Clear the render timeout since we got a successful scan
          clearTimeout(renderTimeout);
          
          // Log success for debugging
          console.log("Barcode successfully scanned!", decodedText, decodedResult);
          
          // Trim any whitespace from the barcode
          const trimmedCode = decodedText.trim();
          
          // Vibrate device if supported (provides physical feedback)
          if (navigator.vibrate) {
            navigator.vibrate(100);
          }
          
          // Play success sound (browser beep)
          try {
            // Use a better success sound with fallback
            const beep = new Audio(
              "data:audio/mp3;base64,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"
            );
            beep.volume = 0.5;
            beep.play();
          } catch (e) {
            // Fall back to a simpler beep if the first one fails
            try {
              const simpleBeep = new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU");
              simpleBeep.volume = 0.5;
              simpleBeep.play();
            } catch (e2) {
              // Ignore errors if audio can't play at all
            }
          }
          
          // Set UI feedback immediately - show the success more prominently
          setSuccessMessage(`Scanned: ${trimmedCode}`);
          setShowSuccessOverlay(true);
          
          // Call the parent component's success callback with cleaned code
          if (onScanSuccess) {
            onScanSuccess(trimmedCode);
          }
          
          // Stop scanning
          setScanning(false);
          
          // Clean up scanner if not continuous
          if (!continuous) {
            try {
              html5QrcodeScanner.clear();
            } catch (e) {
              // Ignore errors
            }
          }
          
          // Hide the success message after 1.5 seconds
          setTimeout(() => {
            setShowSuccessOverlay(false);
          }, 1500);
        }, 
        // Error callback with improved resilience and detailed logging
        (errorMessage) => {
          // Only log actual errors, not routine "no barcode found" messages
          const isRoutineMessage = errorMessage.includes("NotFoundException") || 
                                  errorMessage.includes("No MultiFormat Readers") ||
                                  errorMessage.includes("No barcode or QR code detected");
          
          if (!isRoutineMessage) {
            console.log("Scanner message:", errorMessage);
          }
          
          // Try to provide more helpful error messages based on common issues
          let userMessage = errorMessage;
          let isCriticalError = false;
          
          if (errorMessage.includes("Permission")) {
            userMessage = "Camera permission denied. Please allow camera access and try again.";
            isCriticalError = true;
          } else if (errorMessage.includes("device not found")) {
            // Try to recover by switching cameras
            if (cameras.length > 1 && scanAttempt < 3) {
              // Find the next camera to try
              const currentIndex = cameras.findIndex(camera => camera.id === selectedCamera);
              const nextIndex = (currentIndex + 1) % cameras.length;
              const nextCamera = cameras[nextIndex];
              
              userMessage = `Camera access failed, trying next camera (${nextCamera.label})...`;
              setSelectedCamera(nextCamera.id);
              
              // Try the next camera after a short delay
              setTimeout(() => {
                startScanner();
              }, 1000);
              
              setError(userMessage);
              return;
            } else {
              userMessage = "No working camera found. Please check camera permissions and connections.";
              isCriticalError = true;
            }
          } else if (errorMessage.includes("NotFoundException")) {
            // This is normal - it means no barcode found yet
            return; // Don't show these messages to users
          } else if (errorMessage.includes("NotReadableError")) {
            userMessage = "Camera is in use by another application or not accessible.";
            isCriticalError = true;
          } else if (errorMessage.includes("timeout")) {
            userMessage = "Camera connection timed out. Please try again.";
            isCriticalError = true;
          }
          
          // Only show critical errors to user (not the "no barcode found" errors)
          if (isCriticalError) {
            setError(userMessage);
            setScanning(false);
            if (onScanError) onScanError(errorMessage);
          }
        }
      );
      
      // Customize UI elements with increased timeout to ensure DOM is ready
      const customizeUI = () => {
        // Apply aggressive UI customizations to improve scanning experience
        
        // Hide the header text
        const header = document.getElementById("reader__header_message");
        if (header) {
          header.style.display = "none";
        }
        
        // Focus on scanning by removing distractions
        const fileScanRegion = document.getElementById("reader__filescan_input");
        if (fileScanRegion) {
          // Keep file scan visible as a fallback method
          fileScanRegion.style.display = "block";
        }
        
        // Make sure the scan region is visible
        const scanContainer = document.querySelector("#reader");
        if (scanContainer) {
          scanContainer.style.overflow = "visible"; // Ensure scan area is not cut off
          scanContainer.style.position = "relative"; // Ensure proper positioning
          scanContainer.style.height = "300px"; // Fixed height
          
          // Make dashboard section more visible to highlight file upload option as a better fallback
          const dashboard = document.querySelector("#reader__dashboard");
          if (dashboard) {
            dashboard.style.position = "relative";
            dashboard.style.paddingTop = "10px";
            dashboard.style.paddingBottom = "10px";
            dashboard.style.background = "#f0f7ff";
            dashboard.style.borderTop = "1px solid #cce0ff";
            dashboard.style.borderRadius = "0 0 8px 8px";
            dashboard.style.textAlign = "center";
          }
          
          // Make file selection more prominent as a reliable fallback option
          const fileSelection = document.querySelector("#reader__filescan_input");
          if (fileSelection) {
            fileSelection.style.height = "auto";
            fileSelection.style.margin = "8px 0";
            fileSelection.style.padding = "10px";
            fileSelection.style.border = "2px dashed #cce0ff";
            fileSelection.style.borderRadius = "8px";
            fileSelection.style.background = "#f8faff";
            
            // Add a helpful label
            const fileLabel = document.createElement("div");
            fileLabel.innerText = "Try using file upload to scan a barcode image";
            fileLabel.style.fontSize = "13px";
            fileLabel.style.color = "#3366cc";
            fileLabel.style.marginBottom = "5px";
            fileLabel.style.fontWeight = "bold";
            
            // Insert the label before the file input
            if (fileSelection.firstChild) {
              fileSelection.insertBefore(fileLabel, fileSelection.firstChild);
            }
          }
        }
        
        // Find and adjust the scan region position to be centered
        const scanRegion = document.getElementById("reader__scan_region");
        const readerElement = document.getElementById("reader");
        
        if (scanRegion && readerElement) {
          scanRegion.style.position = "relative";
          scanRegion.style.height = "300px"; // Increased height for better scan area
          scanRegion.style.paddingTop = "10px";
          scanRegion.style.margin = "0 auto";
          scanRegion.style.overflow = "hidden";
          
          // Force full width for better camera view
          scanRegion.style.width = "100%";
          scanRegion.style.maxWidth = "100%";
          
          // Add contrast and focus styling
          scanRegion.style.backgroundColor = "#000"; // Dark background for better contrast
          scanRegion.style.border = "1px solid rgba(255,255,255,0.2)";
          scanRegion.style.borderRadius = "8px";
        }
        
        // Add a scan target overlay to clearly indicate where to place the barcode
        const scanTarget = document.getElementById("scan-target-overlay");
        if (!scanTarget) {
          const targetOverlay = document.createElement("div");
          targetOverlay.id = "scan-target-overlay";
          targetOverlay.style.position = "absolute";
          targetOverlay.style.top = "50%";
          targetOverlay.style.left = "50%";
          targetOverlay.style.transform = "translate(-50%, -50%)";
          
          // Larger target area to make barcode capture easier
          targetOverlay.style.width = "320px";
          targetOverlay.style.height = "160px";
          
          // More visible scan region with pulsing animation
          targetOverlay.style.border = "4px solid rgba(0, 150, 255, 0.8)";
          targetOverlay.style.borderRadius = "8px";
          targetOverlay.style.boxShadow = "0 0 0 2000px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.5)";
          targetOverlay.style.zIndex = "10";
          targetOverlay.style.pointerEvents = "none";
          targetOverlay.style.animation = "pulse 2s infinite"; // Add pulsing animation
          
          // Add the animation keyframes to the document
          const style = document.createElement('style');
          style.innerHTML = `
            @keyframes pulse {
              0% { border-color: rgba(0, 150, 255, 0.8); box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.5); }
              50% { border-color: rgba(255, 255, 255, 0.9); box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.25), inset 0 0 30px rgba(0, 150, 255, 0.8); }
              100% { border-color: rgba(0, 150, 255, 0.8); box-shadow: 0 0 0 2000px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.5); }
            }
          `;
          document.head.appendChild(style);
          
          // Add crosshair lines for better targeting
          // Better targeting lines - horizontal line only for barcode (not vertical)
          const horizontalLine = document.createElement("div");
          horizontalLine.style.position = "absolute";
          horizontalLine.style.left = "0";
          horizontalLine.style.top = "50%";
          horizontalLine.style.width = "100%";
          horizontalLine.style.height = "2px";
          horizontalLine.style.backgroundColor = "rgba(0, 150, 255, 0.8)";
          horizontalLine.style.boxShadow = "0 0 8px rgba(0, 150, 255, 0.8)";
          
          // For barcode scanning, we want to emphasize the horizontal scan line
          // with a laser-like animation to guide the user
          horizontalLine.style.animation = "scanner 2s infinite";
          
          // Add the animation keyframes to the document
          const scannerStyle = document.createElement('style');
          scannerStyle.innerHTML = `
            @keyframes scanner {
              0% { transform: translateY(0px); background-color: rgba(255, 0, 0, 0.8); }
              50% { transform: translateY(0px); background-color: rgba(0, 150, 255, 0.8); }
              100% { transform: translateY(0px); background-color: rgba(255, 0, 0, 0.8); }
            }
          `;
          document.head.appendChild(scannerStyle);
          
          targetOverlay.appendChild(horizontalLine);
          // We'll skip the vertical line for barcode scanning as it's less useful for linear barcodes
          
          // No instruction text as requested by user
          
          // No label append as requested by user
          
          if (scanRegion) {
            scanRegion.appendChild(targetOverlay);
          } else if (readerElement) {
            readerElement.appendChild(targetOverlay);
          }
        }
        
        // Find the scan box via the shaded region and enhance its visibility
        const shadedRegion = document.getElementById("qr-shaded-region");
        if (shadedRegion) {
          // Make the scan region more visually prominent to guide users
          shadedRegion.style.border = "2px dashed rgba(0, 0, 0, 0.3)";
          shadedRegion.style.boxShadow = "0 0 0 2000px rgba(0, 0, 0, 0.1)";
          
          // Ensure scan region is visible and centered
          shadedRegion.style.position = "absolute";
          shadedRegion.style.top = "50%";
          shadedRegion.style.left = "50%";
          shadedRegion.style.transform = "translate(-50%, -50%)";
          
          const corners = shadedRegion.querySelectorAll("div");
          if (corners && corners.length >= 4) {
            corners.forEach(corner => {
              corner.style.border = "4px solid red"; // Thicker border in red
              corner.style.borderRadius = "4px"; 
              corner.style.boxShadow = "0 0 12px rgba(255,0,0,0.7)"; // Add red glow
              corner.style.width = "16px"; // Make corners larger
              corner.style.height = "16px";
            });
          }
        }
        
        // Target the video element for better positioning
        const videoElement = document.querySelector("#reader video");
        if (videoElement) {
          videoElement.style.objectPosition = "center"; // Center the view
          videoElement.style.objectFit = "cover"; // Ensure video fills container
          videoElement.style.maxHeight = "300px"; // Match updated scan region height
          videoElement.style.minHeight = "280px"; // Ensure minimum height
          videoElement.style.width = "100%"; // Full width
          videoElement.style.backgroundColor = "#000"; // Black background
          
          // Remove filters that might interfere with scanning
          videoElement.style.filter = "none";
        }
        
        // No additional instructions as requested by user
      };
      
      // Apply UI customizations immediately and at strategic intervals
      customizeUI(); // Immediate initial run
      
      // Set up a more comprehensive series of UI updates to ensure changes apply
      // as the scanner components load asynchronously
      const uiUpdateIntervals = [100, 300, 500, 750, 1000, 1500, 2000, 3000, 4000];
      uiUpdateIntervals.forEach(interval => {
        setTimeout(customizeUI, interval);
      });
      
      // Also set up a recurring check that runs for the first 5 seconds
      // This helps ensure UI is maintained even as scanner components load/change
      const uiInterval = setInterval(() => {
        customizeUI();
      }, 750);
      
      // Clear the interval after 5 seconds
      setTimeout(() => {
        clearInterval(uiInterval);
      }, 5000);
      
    } catch (err) {
      console.error("Error starting scanner:", err);
      console.error("Error details:", {
        name: err.name,
        message: err.message,
        stack: err.stack
      });
      setError(`Failed to start scanner: ${err.message || "Unknown error"}. Check console for details.`);
      setScanning(false);
    }
  };
  
  // Stop the scanner
  const stopScanner = () => {
    setScanning(false);
    
    if (scanner) {
      try {
        scanner.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };
  
  // Handle manual code entry
  const handleManualEntry = () => {
    if (!manualCode.trim()) return;
    
    setSuccessMessage(`Entered: ${manualCode.trim()}`);
    setShowSuccessOverlay(true);
    
    // Call the success callback
    if (onScanSuccess) {
      onScanSuccess(manualCode.trim());
    }
    
    // Clear the input
    setManualCode("");
    
    // Hide the success message after 1 second
    setTimeout(() => {
      setShowSuccessOverlay(false);
    }, 1000);
  };
  
  // Use native scanner for mobile browsers
  if (useMobileScanner) {
    console.log('🔍 Using BarcodeScannerNative for mobile device');
    return (
      <BarcodeScannerNative
        onScanSuccess={onScanSuccess}
        onScanError={onScanError}
        width={width}
        height={height}
        autoStart={autoStart}
      />
    );
  }
  
  console.log('🔍 Using regular BarcodeScanner for desktop device');
  
  return (
    <Paper elevation={0} sx={{ p: 0, mb: 0 }}>
      
      {/* Scanner title with mode selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'medium' }}>
          Barcode Scanner
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {hasExternalScanner && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant={externalScannerMode ? "contained" : "outlined"}
                size="small"
                onClick={() => setExternalScannerMode(true)}
                disabled={scanning}
                sx={{ minWidth: 120 }}
              >
                External Scanner
              </Button>
              <Button
                variant={!externalScannerMode ? "contained" : "outlined"}
                size="small"
                onClick={() => setExternalScannerMode(false)}
                disabled={scanning}
                sx={{ minWidth: 100 }}
              >
                Camera
              </Button>
            </Box>
          )}
          
          {!externalScannerMode && cameras.length > 1 && (
            <FormControl variant="outlined" size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="camera-select-label">Camera</InputLabel>
              <Select
                labelId="camera-select-label"
                id="camera-select"
                value={selectedCamera}
                onChange={(e) => setSelectedCamera(e.target.value)}
                label="Camera"
                disabled={scanning}
              >
                {cameras.map((camera) => (
                  <MenuItem key={camera.id} value={camera.id}>
                    {camera.label.replace(/\(.*?\)/g, '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            fontSize: '1.1rem',
            '& .MuiAlert-message': {
              fontSize: 'inherit'
            }
          }} 
          onClose={() => setError(null)}
        >
          <strong>Camera Error:</strong><br />
          {error}
        </Alert>
      )}

      
      
      {/* External Scanner Mode */}
      {externalScannerMode ? (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <QrCodeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            <Typography variant="h6" color="primary.main">
              External Scanner Ready
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click the input field below and scan with your external barcode scanner
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ 
          width: width, 
          height: height,
          position: 'relative',
          overflow: 'visible', // Changed from 'hidden' to ensure scan box is fully visible
          backgroundColor: scanning ? 'transparent' : '#f5f5f5',
          border: scanning ? 'none' : '2px dashed #ccc',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '250px' // Ensure minimum height for mobile
        }}>
        {/* Scanner will be rendered here - always present in DOM */}
        <div 
          id="reader" 
          ref={scannerContainerRef}
          style={{ 
            width: '100%', 
            height: '100%',
            position: 'relative',
            zIndex: 1
          }}
          data-debug="scanner-container"
        />
        
        {/* Overlay when scanner not active */}
        {!scanning && !showSuccessOverlay && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            zIndex: 10
          }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Press "Start Scanner" to activate the camera
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={startScanner}
              size="medium"
              sx={{ padding: '8px 16px' }}
            >
              Start Scanner
            </Button>
          </Box>
        )}
        
        {/* Success overlay - more visible and with animated border */}
        <Fade in={showSuccessOverlay} timeout={{ enter: 100, exit: 900 }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(39, 174, 96, 0.7)',
            zIndex: 20,
            animation: 'pulseBorder 1s ease-in-out',
            '@keyframes pulseBorder': {
              '0%': { boxShadow: 'inset 0 0 0 4px rgba(255, 255, 255, 0)' },
              '50%': { boxShadow: 'inset 0 0 0 4px rgba(255, 255, 255, 0.5)' },
              '100%': { boxShadow: 'inset 0 0 0 4px rgba(255, 255, 255, 0)' }
            }
          }}>
            <Box sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              padding: '15px 20px',
              borderRadius: '8px',
              fontWeight: 'bold',
              fontSize: '16px',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#4CAF50"/>
              </svg>
              {successMessage}
            </Box>
          </Box>
        </Fade>
        </Box>
      )}
      
      {/* Removed custom Stop Scanner button - using Html5Qrcode's built-in stop button instead
      {scanning && !externalScannerMode && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={stopScanner}
            fullWidth
          >
            Stop Scanner
          </Button>
        </Box>
      )} */}
      
      {/* Scanner mode note */}
      {!externalScannerMode && !scanning && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
          Click the input field below and scan with your barcode scanner
        </Typography>
      )}
      
      {!externalScannerMode && scanning && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
          Note: Laptop cameras often have difficulty scanning barcodes due to fixed focus and lower resolution.
        </Typography>
      )}
      
      {externalScannerMode && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
          External scanner mode active. Click the input field below and scan.
        </Typography>
      )}
      
      {/* Hide the built-in input field when used in ReceiveBarcodeStep - parent component provides styled input */}
      {/* Uncomment if using BarcodeScanner standalone
      <Box sx={{ mt: 1 }}>
        {externalScannerMode && (
          <Typography variant="body2" color="primary.main" sx={{ mb: 1, fontWeight: 'medium' }}>
            🔍 External scanner detected - Click field below and scan:
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            ref={externalScannerInputRef}
            variant="outlined"
            size="small"
            fullWidth
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && manualCode.trim()) {
                handleManualEntry();
              }
            }}
            placeholder={externalScannerMode ? "Click here and scan with external scanner" : "Scan with external scanner or type manually"}
            sx={{ 
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: externalScannerMode ? 'rgba(25, 118, 210, 0.04)' : 'inherit',
                '&.Mui-focused': {
                  borderColor: 'primary.main',
                  borderWidth: 2
                }
              }
            }}
            autoFocus={true}
            inputProps={{
              'aria-label': externalScannerMode ? 'External scanner input' : 'Manual barcode input',
              'data-testid': 'barcode-input'
            }}
          />
          <Button 
            variant="outlined" 
            size="small"
            disabled={!manualCode.trim()}
            onClick={handleManualEntry}
          >
            Enter
          </Button>
        </Box>
      </Box>
      */}
    </Paper>
  );
}